/**
 * FilterPanel Component
 * Reusable filter panel with search input and dropdown filters
 */

import { Search, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import type { UserRole } from '../types/admin-user.type';

type FilterOption = {
  value: string;
  label: string;
};

type FilterPanelProps = {
  searchValue?: string;
  onSearchChange: (value: string) => void;
  roleValue?: UserRole | '';
  onRoleChange?: (value: UserRole | '') => void;
  statusValue?: string;
  onStatusChange?: (value: string) => void;
  searchPlaceholder?: string;
  showRoleFilter?: boolean;
  showStatusFilter?: boolean;
  statusOptions?: FilterOption[];
};

const ROLE_OPTIONS: FilterOption[] = [
  { value: '', label: 'All Roles' },
  { value: 'CARE_SEEKER', label: 'Care Seeker' },
  { value: 'SERVICE_PROVIDER', label: 'Service Provider' },
  { value: 'FINANCIAL_ADVISOR', label: 'Financial Advisor' },
];

const STATUS_OPTIONS: FilterOption[] = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/**
 * Filter panel component for user list
 * 
 * @param searchValue - Current search term
 * @param onSearchChange - Callback when search changes
 * @param roleValue - Current role filter
 * @param onRoleChange - Callback when role filter changes
 * @param statusValue - Current status filter
 * @param onStatusChange - Callback when status filter changes
 * @param searchPlaceholder - Placeholder text for search input
 * @param showRoleFilter - Show role filter dropdown (default: true)
 * @param showStatusFilter - Show status filter dropdown (default: true)
 * 
 * Features:
 * - Debounced search input (300ms)
 * - Clear search button
 * - Role and status dropdowns
 * - Visual feedback for active filters
 */
export function FilterPanel({
  searchValue = '',
  onSearchChange,
  roleValue = '',
  onRoleChange,
  statusValue = '',
  onStatusChange,
  searchPlaceholder = 'Search by name or email...',
  showRoleFilter = true,
  showStatusFilter = true,
  statusOptions = STATUS_OPTIONS,
}: FilterPanelProps) {
  const [localSearch, setLocalSearch] = useState(searchValue);

  // Sync local search with prop changes
  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== searchValue) {
        onSearchChange(localSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearch, searchValue, onSearchChange]);

  const handleClearSearch = () => {
    setLocalSearch('');
    onSearchChange('');
  };

  const hasActiveFilters = searchValue || roleValue || statusValue;

  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center gap-3">
        {/* Search input */}
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent"
          />
          {localSearch && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-700 transition-colors"
              aria-label="Clear search"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>

        {/* Role filter dropdown */}
        {showRoleFilter && onRoleChange && (
          <select
            value={roleValue}
            onChange={(e) => onRoleChange(e.target.value as UserRole | '')}
            aria-label="Filter by role"
            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent min-w-[160px] ${
              roleValue
                ? 'border-[#026857] bg-[#026857]/5 font-medium'
                : 'border-gray-300'
            }`}
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        {/* Status filter dropdown */}
        {showStatusFilter && onStatusChange && (
          <select
            value={statusValue}
            onChange={(e) => onStatusChange(e.target.value)}
            aria-label="Filter by status"
            className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent min-w-[140px] ${
              statusValue
                ? 'border-[#026857] bg-[#026857]/5 font-medium'
                : 'border-gray-300'
            }`}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        {/* Active filters indicator */}
        {hasActiveFilters && (
          <div className="text-sm text-gray-600 whitespace-nowrap">
            <span className="font-medium text-[#026857]">
              {[searchValue, roleValue, statusValue].filter(Boolean).length}
            </span>
            {' '}
            {[searchValue, roleValue, statusValue].filter(Boolean).length === 1
              ? 'filter'
              : 'filters'}{' '}
            active
          </div>
        )}
      </div>
    </div>
  );
}
